import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Plus, FolderOpen, Trash2, Check, X, MessageSquare, Pencil, Layers,
  ArrowRight, Loader2, ListTodo, Brain, Package,
} from 'lucide-react';
import { GlassCard } from './SharedUI';
import * as projectService from '@/services/projectService';

interface ProjectSpaceProps {
  t: any;
  lang: 'en' | 'zh';
  onOpenConversation: (id: string) => void;
  onNavigate: (tab: string) => void;
}

function formatRelativeTime(iso: string, isZh: boolean): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return isZh ? '刚刚' : 'just now';
  if (mins < 60) return isZh ? `${mins}分钟前` : `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return isZh ? `${hours}小时前` : `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return isZh ? `${days}天前` : `${days}d ago`;
  return new Date(iso).toLocaleDateString(isZh ? 'zh-CN' : 'en-US');
}

export function ProjectSpace({ t, lang, onOpenConversation, onNavigate }: ProjectSpaceProps) {
  const isZh = lang === 'zh';
  const [projects, setProjects] = useState<projectService.Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [conversations, setConversations] = useState<projectService.ProjectConversation[]>([]);
  const [convLoading, setConvLoading] = useState(false);

  // Aggregated views (P1.5): plans + memory + work products
  const [tab, setTab] = useState<'chats' | 'tasks' | 'memory' | 'products'>('chats');
  const [plans, setPlans] = useState<projectService.ProjectPlan[]>([]);
  const [memoryCount, setMemoryCount] = useState<number | null>(null);
  const [workProducts, setWorkProducts] = useState<projectService.ProjectWorkProduct[]>([]);

  // Create dialog state
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);

  // Rename state
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const list = await projectService.listProjects();
      setProjects(list);
      if (!selectedId && list.length > 0) setSelectedId(list[0].id);
    } catch (err) {
      console.warn('[ProjectSpace] Failed to fetch projects:', err);
    } finally {
      setLoading(false);
    }
  // Bug 28: 移除 selectedId 依赖，避免选中项目时重新加载
  }, []);

  const fetchConvos = useCallback(async (pid: string) => {
    setConvLoading(true);
    try {
      const list = await projectService.listProjectConversations(pid);
      setConversations(list);
    } catch (err) {
      console.warn('[ProjectSpace] Failed to fetch project conversations:', err);
    } finally {
      setConvLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (selectedId) fetchConvos(selectedId);
  }, [selectedId, fetchConvos]);

  useEffect(() => {
    if (tab === 'tasks') {
      projectService.listProjectPlans().then(setPlans).catch(() => setPlans([]));
    } else if (tab === 'memory') {
      projectService.getMemoryCount().then((n) => setMemoryCount(n)).catch(() => setMemoryCount(0));
    } else if (tab === 'products') {
      projectService.listWorkProducts().then(setWorkProducts).catch(() => setWorkProducts([]));
    }
  }, [tab]);

  const selected = projects.find((p) => p.id === selectedId) || null;

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const proj = await projectService.createProject(name, newDesc.trim());
      setProjects((prev) => [...prev, proj]);
      setSelectedId(proj.id);
      setShowCreate(false);
      setNewName('');
      setNewDesc('');
    } catch (err) {
      console.error('[ProjectSpace] Create failed:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await projectService.deleteProject(id);
      setProjects((prev) => prev.filter((p) => p.id !== id));
      if (selectedId === id) setSelectedId(null);
    } catch (err) {
      console.error('[ProjectSpace] Delete failed:', err);
    }
  };

  const handleRename = async (id: string) => {
    const name = renameValue.trim();
    if (!name) return;
    try {
      const proj = await projectService.updateProject(id, { name });
      setProjects((prev) => prev.map((p) => (p.id === id ? proj : p)));
      setRenamingId(null);
    } catch (err) {
      console.error('[ProjectSpace] Rename failed:', err);
    }
  };

  return (
    <div className="h-full flex flex-col p-6 gap-4 min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-celestial-saturn/15 border border-celestial-saturn/30 flex items-center justify-center">
            <Layers size={18} className="text-celestial-saturn" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white/90">{isZh ? '项目空间' : 'Project Spaces'}</h2>
            <p className="text-xs text-white/40">{isZh ? '把对话归入项目，作为你的工作容器' : 'Group your chats into project containers'}</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-celestial-saturn/20 to-celestial-saturn/10 border border-celestial-saturn/30 text-celestial-saturn text-sm font-bold hover:border-celestial-saturn/50 transition-all"
        >
          <Plus size={15} /> {isZh ? '新建项目' : 'New Project'}
        </button>
      </div>

      <div className="flex-1 flex gap-4 min-h-0">
        {/* Left: project list */}
        <div className="w-[300px] flex-shrink-0 flex flex-col gap-2 min-h-0">
          <div className="text-[11px] font-bold uppercase tracking-widest text-white/30 px-2">
            {isZh ? '我的项目' : 'My Projects'}
          </div>
          <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 space-y-2">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="text-celestial-saturn animate-spin" />
              </div>
            ) : projects.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-white/20">
                {isZh ? '还没有项目，点击右上角新建' : 'No projects yet'}
              </div>
            ) : (
              <AnimatePresence initial={false}>
                {projects.map((p) => (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div
                      onClick={() => setSelectedId(p.id)}
                      className={`group relative px-3 py-3 rounded-2xl cursor-pointer transition-all duration-200 border ${
                        selectedId === p.id
                          ? 'bg-white/[0.06] border-white/15'
                          : 'border-transparent hover:bg-white/[0.03]'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                        {renamingId === p.id ? (
                          <input
                            autoFocus
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRename(p.id);
                              if (e.key === 'Escape') setRenamingId(null);
                            }}
                            onBlur={() => handleRename(p.id)}
                            className="flex-1 bg-black/30 border border-celestial-saturn/40 rounded-lg px-2 py-1 text-sm text-white outline-none"
                          />
                        ) : (
                          <span className="flex-1 text-sm font-bold text-white/80 truncate">{p.name}</span>
                        )}
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => { e.stopPropagation(); setRenamingId(p.id); setRenameValue(p.name); }}
                            className="p-1 rounded-lg text-white/30 hover:text-celestial-saturn hover:bg-white/[0.06]"
                            title={isZh ? '重命名' : 'Rename'}
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }}
                            className="p-1 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10"
                            title={isZh ? '删除' : 'Delete'}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                      {p.desc && <div className="text-[11px] text-white/35 mt-1 line-clamp-1 pl-5.5">{p.desc}</div>}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </div>
        </div>

        {/* Right: selected project conversations */}
        <div className="flex-1 min-w-0">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-white/20 gap-3">
              <FolderOpen size={40} className="text-white/15" />
              <p className="text-sm">{isZh ? '选择一个项目查看其中的对话' : 'Select a project to view its chats'}</p>
            </div>
          ) : (
            <GlassCard className="h-full p-6 rounded-[2rem] flex flex-col min-h-0" hoverEffect={false}>
              <div className="flex items-center gap-3 flex-shrink-0 pb-4 border-b border-white/[0.06]">
                <span className="w-4 h-4 rounded-full" style={{ backgroundColor: selected.color }} />
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold text-white/90 truncate">{selected.name}</h3>
                  {selected.desc && <p className="text-xs text-white/40 truncate">{selected.desc}</p>}
                </div>
              </div>

              {/* Aggregated view tabs */}
              <div className="flex items-center gap-1 mt-4 flex-shrink-0">
                {([
                  { id: 'chats' as const, label: isZh ? '对话' : 'Chats', Icon: MessageSquare },
                  { id: 'tasks' as const, label: isZh ? '任务' : 'Tasks', Icon: ListTodo },
                  { id: 'products' as const, label: isZh ? '产物' : 'Products', Icon: Package },
                  { id: 'memory' as const, label: isZh ? '记忆' : 'Memory', Icon: Brain },
                ]).map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    onClick={() => setTab(id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      tab === id
                        ? 'bg-celestial-saturn/15 text-celestial-saturn'
                        : 'text-white/40 hover:text-white/70 hover:bg-white/[0.04]'
                    }`}
                  >
                    <Icon size={13} /> {label}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 mt-3 space-y-2">
                {tab === 'chats' && (convLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 size={20} className="text-celestial-saturn animate-spin" />
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="px-3 py-8 text-center text-xs text-white/20">
                    {isZh ? '该项目还没有对话' : 'No conversations in this project'}
                  </div>
                ) : (
                  conversations.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => onOpenConversation(c.id)}
                      className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left hover:bg-white/[0.04] border border-transparent hover:border-white/10 transition-all"
                    >
                      <MessageSquare size={15} className="text-white/30 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white/75 truncate">{c.title || (isZh ? '未命名对话' : 'Untitled')}</div>
                        <div className="text-[10px] text-white/25 mt-0.5">
                          {c.messageCount} {isZh ? '条消息' : 'msgs'} · {formatRelativeTime(c.lastActiveAt, isZh)}
                        </div>
                      </div>
                      <ArrowRight size={15} className="text-white/20 flex-shrink-0" />
                    </button>
                  ))
                ))}

                {tab === 'tasks' && (
                  plans.length === 0 ? (
                    <div className="px-3 py-8 text-center text-xs text-white/20">
                      {isZh ? '暂无进行中的任务计划' : 'No active plans'}
                    </div>
                  ) : plans.map((p) => {
                    const done = (p.steps || []).filter((s) => s.status === 'done' || s.status === 'skipped').length;
                    const pct = p.steps?.length ? Math.round((done / p.steps.length) * 100) : 0;
                    return (
                      <div key={p.id} className="px-3 py-3 rounded-xl border border-white/[0.06] bg-white/[0.02]">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold text-white/80 truncate">{p.title}</span>
                          <span className="text-[10px] font-black text-celestial-saturn">{pct}%</span>
                        </div>
                        <div className="w-full h-1 rounded-full bg-white/10 overflow-hidden mt-2">
                          <div className="h-full bg-celestial-saturn" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="text-[10px] text-white/30 mt-1.5">
                          {(p.steps || []).length} {isZh ? '个步骤' : 'steps'} · {formatRelativeTime(p.updatedAt, isZh)}
                        </div>
                      </div>
                    );
                  })
                )}

                {tab === 'products' && (
                  workProducts.length === 0 ? (
                    <div className="px-3 py-8 text-center text-xs text-white/20">
                      {isZh ? '暂无 AI 生成的产物记录' : 'No AI-generated work products yet'}
                    </div>
                  ) : workProducts.map((wp) => (
                    <div key={wp.id} className="px-3 py-3 rounded-xl border border-white/[0.06] bg-white/[0.02]">
                      <div className="flex items-center gap-2">
                        <Package size={14} className="text-celestial-saturn flex-shrink-0" />
                        <span className="text-sm font-bold text-white/80 truncate flex-1">{wp.task}</span>
                        <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-celestial-saturn/15 text-celestial-saturn uppercase">
                          {wp.deliverableType}
                        </span>
                      </div>
                      {wp.finalOutput && (
                        <div className="text-[11px] text-white/40 mt-1.5 line-clamp-2 pl-6">{wp.finalOutput}</div>
                      )}
                      <div className="text-[10px] text-white/30 mt-1.5 pl-6 flex items-center gap-3">
                        <span>{wp.acceptanceCriteriaCount} {isZh ? '项验收标准' : 'criteria'}</span>
                        <span>·</span>
                        <span>{formatRelativeTime(wp.createdAt, isZh)}</span>
                      </div>
                    </div>
                  ))
                )}

                {tab === 'memory' && (
                  <div className="px-3 py-8 text-center">
                    <Brain size={32} className="text-white/15 mx-auto mb-3" />
                    <div className="text-sm font-bold text-white/70">
                      {memoryCount === null ? (
                        <Loader2 size={18} className="animate-spin inline text-celestial-saturn" />
                      ) : (
                        <>{(isZh ? '你的记忆库共有 ' : 'You have ') + memoryCount + (isZh ? ' 条记忆' : ' memories')}</>
                      )}
                    </div>
                    <p className="text-[11px] text-white/30 mt-2 max-w-[280px] mx-auto">
                      {isZh ? '记忆为跨项目全局共享，是小枫长期陪伴你积累的认知资产。' : 'Memories are shared globally across projects — your agent\'s long-term assets.'}
                    </p>
                  </div>
                )}
              </div>
            </GlassCard>
          )}
        </div>
      </div>

      {/* Create modal */}
      <AnimatePresence>
        {showCreate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setShowCreate(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-[min(420px,92vw)] bg-[#0b1020] border border-white/10 rounded-2xl p-6 space-y-4"
            >
              <h3 className="text-base font-bold text-white/90">{isZh ? '新建项目' : 'New Project'}</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-white/40">{isZh ? '项目名称' : 'Name'}</label>
                  <input
                    autoFocus
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                    placeholder={isZh ? '例如：趁迅拓客 v2' : 'e.g. ChenXun v2'}
                    className="w-full mt-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-celestial-saturn/50"
                  />
                </div>
                <div>
                  <label className="text-xs text-white/40">{isZh ? '描述（可选）' : 'Description (optional)'}</label>
                  <input
                    value={newDesc}
                    onChange={(e) => setNewDesc(e.target.value)}
                    placeholder={isZh ? '这个项目的目标…' : 'Goal of this project…'}
                    className="w-full mt-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-celestial-saturn/50"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-lg text-sm text-white/50 hover:bg-white/[0.05]">
                  {isZh ? '取消' : 'Cancel'}
                </button>
                <button
                  onClick={handleCreate}
                  disabled={!newName.trim() || creating}
                  className="px-4 py-2 rounded-lg text-sm font-bold bg-celestial-saturn text-black disabled:opacity-40 hover:scale-105 transition-transform"
                >
                  {creating ? <Loader2 size={14} className="animate-spin inline" /> : (isZh ? '创建' : 'Create')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
